// sonuc (hesaplaZamanAsimi çıktısı) → rapor için tek normalize veri nesnesi.
// PDF ve Word AYNI nesneyi tüketir; böylece bilgi tekrarı/çelişkisi olmaz.

import { formatKisa, formatUzun, formatSure, kalanSure, durmaToplamIfade } from '../formatlayicilar';
import { buildAsamalar, farkMetni } from '../asamalar';

const ORAN_METNI = { '12-15': '× 1/2', '15-18': '× 2/3' };

// Raporun her sayfasının altına eklenen hukuki feragat (sorumluluk reddi).
export const RAPOR_FERAGAT =
  'Bu rapor yalnızca bilgilendirme amacıyla üretilmiştir; hukuki görüş veya mütalaa niteliği taşımaz, ' +
  'bağlayıcı değildir ve hiçbir hukuki sonuç doğurmaz. İçeriğinden doğabilecek sonuçlardan dolayı hukuki ' +
  'sorumluluk kabul edilmez. Nihai değerlendirme yürürlükteki mevzuat ve içtihatlara uygun bir şekilde ilgili hukukçular tarafından yapılmalıdır.';

const VARSAYILAN_SECIMLER = { sureOlusumu: true, asamalar: true };

/** sonuc → { doldu, durumMetni, effectiveUzun, gerekce, kunye[], sureOlusumu[], asamalar[], secimler } */
export function buildRaporVerisi(sonuc, secimler = VARSAYILAN_SECIMLER) {
  const yas = sonuc.yasGrubuBilgisi || {};
  const doldu = sonuc.zamanAsimiDoldu;
  const indirimVar = yas.grup && yas.grup !== 'yetiskin';

  // Kalan süre — ekran ve rapor ortak biçimi (yıl/ay/gün + parantezde ham gün).
  const ks = kalanSure(sonuc);

  // Gerekçe metni — SonucGosterimi.js ile birebir aynı üç-yollu mantık.
  const gerekce = sonuc.capApplied
    ? 'Olağan tarih üst sınırı aştığından TCK 67/4 mutlak üst sınır tarihi esas alınmıştır.'
    : sonuc.hasKesme
    ? 'Kesilmelerle yeniden işleyen olağan süre esas alınmıştır.'
    : 'Kesilme bulunmadığından olağan zamanaşımı tarihi esas alınmıştır.';

  const kesmeDeger =
    sonuc.etkiliKesmeSayisi > 0
      ? `${sonuc.etkiliKesmeSayisi} adet · son ${formatKisa(sonuc.sonKesilmeTarihi)}`
      : 'Yok';
  const durmalar = (sonuc.timelineModel.durmalar || []).map((d) => ({ bas: new Date(d.start), bit: new Date(d.end) }));
  const durmaDeger = sonuc.toplamDurmaSuresi > 0 ? durmaToplamIfade(durmalar, sonuc.toplamDurmaSuresi) : 'Yok';

  return {
    doldu,
    durumMetni: doldu ? 'Zamanaşımı dolmuş' : `Kalan: ${ks.sure}`,
    durumDetay: ks.cokParcali ? ks.gun : '',
    kalanGun: sonuc.kalanGun,
    effectiveUzun: formatUzun(sonuc.effective),
    effectiveKisa: formatKisa(sonuc.effective),
    gecerliTur: sonuc.capApplied ? 'olağanüstü zamanaşımı tarihi' : 'olağan zamanaşımı tarihi',
    gerekce,

    // Suç & süre bilgileri (künye) — her veri tek yerde.
    kunye: [
      { etiket: 'Suç tarihi', deger: formatKisa(sonuc.timelineModel.sucTarihi) },
      { etiket: 'Yaş grubu', deger: yas.etiket || '—' },
      { etiket: 'Uygulanan süre', deger: formatSure(yas.ayarlanmisSure) },
      { etiket: 'Hesaplama tarihi', deger: formatKisa(sonuc.bugun) },
      { etiket: 'Olağan Zamanaşımı Tarihi', deger: formatKisa(sonuc.olaganZamanAsimi) },
      { etiket: 'Olağanüstü Z.A. Tarihi (67/4)', deger: formatKisa(sonuc.olaganustuZamanAsimi) },
      { etiket: 'Kesme (67/2)', deger: kesmeDeger },
      { etiket: 'Toplam durma (67/1)', deger: durmaDeger },
    ],

    // Sürenin oluşumu.
    sureOlusumu: [
      { etiket: 'Kanuni süre (66/1)', deger: formatSure(yas.normalSure) },
      {
        etiket: 'Yaş indirimi (66/2)',
        deger: indirimVar ? ORAN_METNI[yas.grup] || '—' : 'Yok',
        alt: indirimVar ? yas.etiket : '18 yaş ve üzeri',
      },
      { etiket: 'Uygulanan süre', deger: formatSure(yas.ayarlanmisSure) },
      { etiket: '67/4 uzaması (½)', deger: formatSure(yas.kanuniSureninYarisi), alt: 'üst sınır için' },
    ],

    // Aşama aşama geçen süre — önceden biçimlenmiş satırlar (PDF/Word ortak tüketir).
    asamalar: buildAsamalar(sonuc).map((a) => ({
      from: a.fromLabel,
      to: a.toLabel,
      ref: a.ref || '',
      sure: farkMetni(a.fark),
      gun: a.fark.gunSayisi,
      accent: !!a.accent,
      doldu: !!a.doldu,
    })),

    // Hangi opsiyonel bölümlerin rapora gireceği.
    secimler: { ...VARSAYILAN_SECIMLER, ...(secimler || {}) },
  };
}

/** Çıktı dosyası adı: "Zamanasimi_Raporu_15-01-2020.pdf". */
export function raporDosyaAdi(sonuc, ext) {
  const t = new Date(sonuc.timelineModel.sucTarihi);
  const d = `${String(t.getDate()).padStart(2, '0')}-${String(t.getMonth() + 1).padStart(2, '0')}-${t.getFullYear()}`;
  return `Zamanasimi_Raporu_${d}.${ext}`;
}
